import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useNavigate } from "@/hooks/useNavigate";
import { Search, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { type Store } from "@/api/storeService";
import {
  searchHybrid,
  hitToStore,
  type SelectedFacets,
} from "@/api/searchService";
import { categoryService } from "@/api/categoryService";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { AddStoreModal } from "@/components/AddStoreModal";
import { StoreListCard, StoreListCardSkeleton } from "@/components/StoreListCard";
import FacetSidebar from "@/components/FacetSidebar";
import QuickFilters from "@/components/QuickFilters";
import { toArabicNumerals } from "@/lib/arabicNumerals";

const PAGE_SIZE = 12;

// Below this top-hit ranking score, the best "match" is only a partial word
// overlap and not the store the user asked for. Calibrated against prod on
// 2026-08-18: real name matches score 0.86–1.00 (الفرجاني 1.00, كارفور 0.97,
// صيدليات سيف 0.86) while non-matches land at 0.36–0.67 (هايبر وان → الفرجاني
// 0.36, مكتبة الشروق → a different bookstore 0.67). 0.75 sits in that gap.
const WEAK_MATCH_SCORE = 0.75;

// "best" = merchandised/default order; "relevance" = no sort param (Meili ranking).
type SortKey = "best" | "newest" | "rating" | "reviews" | "relevance";

type StoresProps = {
  lockedCategoryId?: string;
  defaultSort?: string[];
  initialStores?: Store[];
  headerSlot?: React.ReactNode;
  // Rendered full width after the results block, regardless of whether the store list loaded:
  // the category page puts server-rendered review text here, which must survive a failed seed.
  footerSlot?: React.ReactNode;
};

const Stores = ({ lockedCategoryId, defaultSort, initialStores, headerSlot, footerSlot }: StoresProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, showAuthModal } = useAuth();
  const { trackNavigation } = usePostHogTracking();
  // Seed the query-derived state from the URL synchronously. When arriving via
  // client-side navigation from another page (e.g. home search chips →
  // /stores?q=chip), a "" seed made the first loadStores() fire with an empty
  // term and briefly render ALL stores before the real query landed. Seeding
  // here keeps the loading skeleton up until the correct results arrive.
  const initialQuery = (searchParams.get("q") ?? "").trim();
  // /stores?category=<slug> (the category page's «كل متاجر الفئة» link): that category's facet is
  // pre-selected once the categories list resolves the slug to an id. The first load waits for it
  // so the page never flashes unfiltered results.
  const [pendingCategorySlug, setPendingCategorySlug] = useState<string | null>(
    (searchParams.get("category") ?? "").trim() || null
  );
  // Read by the mount-time categories loader without making it reactive (exhaustive-deps).
  const pendingCategoryRef = useRef<string | null>(pendingCategorySlug);
  // SSR-seeded renders show cards, not skeletons — computed identically on the
  // server and the first client render (no hydration mismatch).
  const [stores, setStores] = useState<Store[]>(initialStores ?? []);
  const [loading, setLoading] = useState(!initialStores);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  // The grid fetches from this settled value, not from every keystroke: a 400ms
  // debounce commits searchTerm → debouncedSearchTerm, so one hybrid search
  // fires per typing pause instead of one per keypress (D-01).
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialQuery);
  // True once any load has succeeded (or the page was SSR-seeded). Skeletons
  // render only before the first-ever load — retyping keeps previous results
  // visible while the newer search is in flight.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(!!initialStores);
  const [sortKey, setSortKey] = useState<SortKey>(initialQuery ? "relevance" : "reviews");
  const [currentPage, setCurrentPage] = useState(1);
  const [facetDistribution, setFacetDistribution] = useState<{
    categoryIds?: Record<string, number>;
    hasFacebook?: Record<string, number>;
    hasInstagram?: Record<string, number>;
    hasWebsite?: Record<string, number>;
  } | null>(null);
  const [totalHits, setTotalHits] = useState(0);
  const [selectedFacets, setSelectedFacets] = useState<SelectedFacets>({
    categoryIds: [],
    hasFacebook: false,
    hasInstagram: false,
    hasWebsite: false,
    minRating: 0,
  });
  const [addStoreOpen, setAddStoreOpen] = useState(false);
  // True when page 1 holds no store that actually matches the query by name:
  // either nothing matched lexically at all (every hit came from the semantic
  // side of the hybrid merge) or the best match is a weak partial-word overlap.
  // null = not measured (SSR-seeded render, browse mode, request in flight).
  const [noRealMatch, setNoRealMatch] = useState<boolean | null>(null);
  // Built once from the full categories API (keys are category ObjectIds, matching
  // facetDistribution.categoryIds keys).
  const [categoryIdToName, setCategoryIdToName] = useState<Map<string, string>>(new Map());

  // Monotonic sequence for in-flight searches: an older response must never
  // overwrite a newer one's state (or emit analytics) — see loadStores.
  const requestSeqRef = useRef(0);

  // Based on the SETTLED query so the sortKey reset (relevance↔reviews) fires
  // once per debounced query, not per keystroke.
  const hasQuery = debouncedSearchTerm.trim().length > 0;

  // Maps the select key to Meilisearch sort expressions. "best" with a
  // defaultSort prop (locked-category pages) means the merchandised order,
  // not plain yetfarragRating:desc. "relevance" sends no sort param.
  const resolveSort = useCallback(
    (key: SortKey): string[] | undefined => {
      switch (key) {
        case "best":
          return defaultSort ?? ["yetfarragRating:desc"];
        case "newest":
          return ["createdAt:desc"];
        case "rating":
          return ["rating:desc", "totalReviews:desc"];
        case "reviews":
          return ["totalReviews:desc"];
        case "relevance":
          return undefined;
      }
    },
    [defaultSort]
  );

  const loadStores = useCallback(
    async () => {
      // Stale-response guard: capture this request's sequence number up front;
      // any await that resolves after a newer request started bails out before
      // touching state or analytics.
      const seq = ++requestSeqRef.current;
      try {
        setLoading(true);
        const term = debouncedSearchTerm;
        // The locked category id is pinned into EVERY call (deduped against user facets).
        const mergedCategoryIds = lockedCategoryId
          ? [lockedCategoryId, ...selectedFacets.categoryIds.filter((id) => id !== lockedCategoryId)]
          : selectedFacets.categoryIds.length > 0
            ? selectedFacets.categoryIds
            : undefined;

        const page = currentPage;
        const response = await searchHybrid({
          q: term.trim(),
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          sort: resolveSort(sortKey),
          categoryIds: mergedCategoryIds,
          hasFacebook: selectedFacets.hasFacebook || undefined,
          hasInstagram: selectedFacets.hasInstagram || undefined,
          hasWebsite: selectedFacets.hasWebsite || undefined,
          minRating: selectedFacets.minRating || undefined,
          // Scores feed the backend's topScore telemetry; the all-semantic
          // verdict below relies on semanticHitCount, which is always returned.
          showRankingScore: term.trim().length > 0 || undefined,
        });
        // A newer request superseded this one — its response owns all state.
        if (seq !== requestSeqRef.current) return;
        const hits = response.data.hits;
        setStores(hits.map(hitToStore));
        setHasLoadedOnce(true);
        setFacetDistribution(response.data.facetDistribution ?? null);
        setTotalHits(response.data.totalHits);
        // Only page 1 of a real query is a meaningful "does this store exist"
        // signal — deeper pages are always semantic tail. Meili orders lexical
        // hits ahead of semantic ones at this ratio, so the top hit's score is
        // the lexical score whenever any lexical hit exists.
        const semanticHitCount = response.data.semanticHitCount;
        const topScore = hits[0]?._rankingScore;
        setNoRealMatch(
          term.trim().length > 0 && page === 1
            ? hits.length === 0 ||
              (semanticHitCount !== undefined && semanticHitCount >= hits.length) ||
              (topScore !== undefined && topScore < WEAK_MATCH_SCORE)
            : null
        );
        // Fired on every completed load — query, facet, sort, and page changes
        // are all meaningful search interactions.
        const facetsActive =
          (selectedFacets.categoryIds.length > 0 ? 1 : 0) +
          (selectedFacets.hasFacebook ? 1 : 0) +
          (selectedFacets.hasInstagram ? 1 : 0) +
          (selectedFacets.hasWebsite ? 1 : 0) +
          (selectedFacets.minRating > 0 ? 1 : 0);
        trackNavigation.searchPerformed(term.trim(), hits.length, {
          surface: lockedCategoryId ? "category_page" : "stores_page",
          facets_active: facetsActive,
          sort_key: sortKey,
          page,
          total_hits: response.data.totalHits,
        });
        setError(null);
      } catch (err) {
        // Stale failures are irrelevant — a newer request is already in flight.
        if (seq !== requestSeqRef.current) return;
        setError(err instanceof Error ? err.message : "حدث خطأ غير متوقع");
      } finally {
        // The newest request owns the loading flag; a stale one must not clear it.
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    [currentPage, debouncedSearchTerm, sortKey, selectedFacets, lockedCategoryId, resolveSort, trackNavigation]
  );

  // Fire category_viewed once per locked category (name arrives async from the
  // categories API; slug comes from the /category/@slug URL).
  const categoryViewedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!lockedCategoryId || typeof window === "undefined") return;
    const name = categoryIdToName.get(lockedCategoryId);
    if (!name || categoryViewedFor.current === lockedCategoryId) return;
    categoryViewedFor.current = lockedCategoryId;
    const slugMatch = window.location.pathname.match(/^\/category\/([^/]+)/);
    const slug = slugMatch ? decodeURIComponent(slugMatch[1]) : "";
    trackNavigation.categoryViewed(name, slug, { category_id: lockedCategoryId });
  }, [lockedCategoryId, categoryIdToName, trackNavigation]);

  // Loads the canonical id → Arabic-name map for facet labels. Failure is non-fatal:
  // FacetSidebar falls back to rendering the raw id.
  const loadCategoryNameMap = async () => {
    try {
      const cats = await categoryService.getCategories();
      setCategoryIdToName(new Map(cats.map((c) => [c.id, c.title.ar])));
      const slug = pendingCategoryRef.current;
      if (slug) {
        const match = cats.find((c) => c.slug === slug);
        if (match) setSelectedFacets((prev) => ({ ...prev, categoryIds: [match.id] }));
        pendingCategoryRef.current = null;
        setPendingCategorySlug(null);
      }
    } catch (err) {
      console.error("Error loading category names:", err);
      // An unknown slug or a failed list must never hold the results back.
      pendingCategoryRef.current = null;
      setPendingCategorySlug(null);
    }
  };

  useEffect(() => {
    loadCategoryNameMap();
  }, []);

  // Keep the search box in sync with the URL query. Client-side navigations to
  // /stores?q=... (e.g. home search chips) can re-render this page without
  // remounting, so the mount-time seed above isn't enough — without this the
  // box keeps the previous query and loadStores() fetches stale results. The
  // functional updater no-ops when unchanged, so it never clobbers in-progress
  // typing (typing doesn't change the URL, so searchParams stays put).
  useEffect(() => {
    const q = (searchParams.get("q") ?? "").trim();
    setSearchTerm((prev) => (prev === q ? prev : q));
    // URL-driven queries bypass the debounce — client-side navs (home chips →
    // /stores?q=…) must load instantly, not 400ms later.
    setDebouncedSearchTerm((prev) => (prev === q ? prev : q));
  }, [searchParams]);

  // Debounce typing → settled query (~400ms). Committing the term and resetting
  // to page 1 happen in one batched update, so the loadStores effect fires once
  // per settled query (searchPerformed analytics therefore fires once too).
  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return;
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // When q transitions empty↔non-empty, reset the sort key to its contextual
  // default: "reviews" (number of ratings) for browse, "relevance" for typed queries.
  useEffect(() => {
    setSortKey(hasQuery ? "relevance" : "reviews");
  }, [hasQuery]);

  useEffect(() => {
    if (pendingCategorySlug) return;
    loadStores();
  }, [loadStores, pendingCategorySlug]);

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    if (trimmed) {
      navigate(`/stores?q=${encodeURIComponent(trimmed)}`);
    }
    // Flush the debounce: commit the term and land on page 1 immediately. When
    // nothing changed, state equality means no refetch — results already show.
    setSearchTerm(trimmed);
    setDebouncedSearchTerm(trimmed);
    setCurrentPage(1);
  };

  const handleFacetChange = (facets: SelectedFacets) => {
    setSelectedFacets(facets);
    setCurrentPage(1);
  };

  const handleClearFacets = () => {
    setSelectedFacets({ categoryIds: [], hasFacebook: false, hasInstagram: false, hasWebsite: false, minRating: 0 });
    setCurrentPage(1);
  };

  const handleSortChange = (key: string) => {
    setSortKey(key as SortKey);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Show the "add it yourself" prompt only when we can trust the signal: a real
  // query, unsorted (relevance) order, page 1, and a settled response with no
  // real match. Custom sorts reorder hits, so neither the merge origin nor the
  // top score maps to the visible order under them.
  const showAddStorePrompt =
    hasQuery && sortKey === "relevance" && currentPage === 1 && !loading && !error && noRealMatch === true;

  const handleAddStoreClick = () => {
    if (!user) {
      showAuthModal({ trigger: "zero_results" });
    } else {
      setAddStoreOpen(true);
    }
  };

  const facetSidebar = (
    <FacetSidebar
      facetDistribution={{
        categoryIds: facetDistribution?.categoryIds,
        hasFacebook: facetDistribution?.hasFacebook,
        hasInstagram: facetDistribution?.hasInstagram,
        hasWebsite: facetDistribution?.hasWebsite,
      }}
      categoryIdToName={categoryIdToName}
      selectedFacets={selectedFacets}
      onFacetChange={handleFacetChange}
      onClearAll={handleClearFacets}
      hideCategoryFacet={!!lockedCategoryId}
    />
  );

  return (
    <div className="min-h-screen bg-white font-arabic">
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        {headerSlot ?? (
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 font-arabic mb-4">
              اكتشف المتاجر
            </h1>
            <p className="text-gray-600 font-arabic text-lg max-w-2xl mx-auto">
              استكشف مجموعة متنوعة من المتاجر على منصات التواصل الاجتماعي واعثر
              على ما تبحث عنه
            </p>
          </div>
        )}

        {/* Search and Sort */}
        <div className="bg-warmBeige-50 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search — no autocomplete here: the results grid IS the live feedback (D-01) */}
            <div className="md:col-span-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="دوّر على متجر أو فئة…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                      if (e.key === "Escape") e.currentTarget.blur();
                    }}
                    className="font-arabic w-full"
                  />
                </div>
                <Button
                  onClick={handleSearch}
                  className="bg-warmOrange-500 hover:bg-warmOrange-600"
                  aria-label="ابحث عن متاجر"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Sort — always visible */}
            <div>
              <Select dir="rtl" value={sortKey} onValueChange={handleSortChange}>
                <SelectTrigger className="font-arabic">
                  <SelectValue placeholder="ترتيب حسب" />
                </SelectTrigger>
                <SelectContent>
                  {hasQuery && <SelectItem value="relevance">الأكثر صلة</SelectItem>}
                  <SelectItem value="best">الأفضل</SelectItem>
                  <SelectItem value="newest">الأحدث</SelectItem>
                  <SelectItem value="rating">الأعلى تقييماً</SelectItem>
                  <SelectItem value="reviews">عدد التقييمات</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Quick filter chips */}
        <QuickFilters
          selectedFacets={selectedFacets}
          onFacetChange={handleFacetChange}
        />

        {/* Mobile filter trigger */}
        <div className="flex justify-end mb-4 md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="font-arabic min-h-[44px]">
                <SlidersHorizontal className="w-4 h-4 me-2" />
                فلتر
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto font-arabic">
              <SheetHeader>
                <SheetTitle className="font-arabic text-right">خيارات البحث</SheetTitle>
              </SheetHeader>
              <div className="py-4">{facetSidebar}</div>
              <SheetFooter>
                <Button className="w-full bg-warmOrange-500 hover:bg-warmOrange-600 font-arabic">
                  تطبيق الفلاتر
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Loading — skeletons only before the first-ever load; retyping keeps
            the previous results rendered while the newer search is in flight */}
        {loading && !hasLoadedOnce && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <StoreListCardSkeleton key={index} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 font-arabic text-lg">{error}</p>
            <Button
              onClick={() => loadStores()}
              className="mt-4 bg-warmOrange-500 hover:bg-warmOrange-600"
            >
              إعادة المحاولة
            </Button>
          </div>
        )}

        {/* Stores Grid — stays rendered during in-flight refetches (subtle dim
            as the only loading hint after the first load) */}
        {hasLoadedOnce && !error && (
          <div className={`flex gap-6 ${loading ? "opacity-60 transition-opacity" : ""}`}>
            {/* Desktop facet sidebar — first in DOM = reading-start (right) side in RTL */}
            <div className="hidden md:block w-64 shrink-0 animate-fade-in">{facetSidebar}</div>

            <div className="flex-1">
              {/* Nothing matched the query by name — offer to add the store instead
                  of presenting the semantic tail as if it were an answer. */}
              {showAddStorePrompt && (
                <div className="bg-warmBeige-50 border border-warmOrange-200 rounded-xl p-6 mb-8 text-center">
                  <p className="text-gray-900 font-arabic text-lg font-semibold">
                    مش لاقيين «{debouncedSearchTerm.trim()}» عندنا
                  </p>
                  <p className="text-gray-600 font-arabic text-sm mt-2 max-w-xl mx-auto">
                    ضيف المتجر في دقيقة واحدة، ابعتلنا لينك صفحته واحنا هنكمّل الباقي.
                  </p>
                  <Button
                    onClick={handleAddStoreClick}
                    className="mt-4 bg-warmOrange-500 hover:bg-warmOrange-600 font-arabic"
                  >
                    ضيف المتجر
                  </Button>
                  <p className="text-gray-500 font-arabic text-xs mt-3">
                    مجاني · بيظهر بعد مراجعة سريعة
                  </p>
                </div>
              )}

              {/* Label the semantic tail honestly when it isn't a real match. */}
              {showAddStorePrompt && stores.length > 0 && (
                <h2 className="text-gray-700 font-arabic text-base font-semibold mb-4">
                  نتائج قد تكون ذات صلة
                </h2>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {stores.map((store) => (
                  <StoreListCard key={store._id} store={store} />
                ))}
              </div>

              {/* Empty state — suppressed when the add-store prompt already
                  explains the miss (browse/filters keep the original copy). */}
              {stores.length === 0 && !showAddStorePrompt && (
                <div className="text-center py-12">
                  <p className="text-gray-600 font-arabic text-lg font-semibold">لا توجد نتائج</p>
                  <p className="text-gray-500 font-arabic text-sm mt-2">
                    لم يتم العثور على متاجر مطابقة لبحثك، جرّب كلمات أخرى أو غيّر الفلاتر
                  </p>
                </div>
              )}

              {/* Offset pagination via totalHits */}
              {totalHits > PAGE_SIZE && (
                <div className="flex justify-center items-center gap-4 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="font-arabic"
                  >
                    السابق
                  </Button>
                  <span className="text-sm text-gray-500 font-arabic tabular-nums">
                    {`صفحة ${toArabicNumerals(currentPage)} من ${toArabicNumerals(Math.ceil(totalHits / PAGE_SIZE))}`}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage * PAGE_SIZE >= totalHits}
                    className="font-arabic"
                  >
                    التالي
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {footerSlot}
      </div>

      <Footer variant="minimal" />

      <AddStoreModal isOpen={addStoreOpen} onClose={() => setAddStoreOpen(false)} />
    </div>
  );
};

export default Stores;
